import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatOptionModule } from '@angular/material/core';
import { MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { map } from 'rxjs';
import { getIsNotNullable } from 'src/app/core/nullable-utils';
import {
  FeatureFlagConfig,
  FeatureFlagKey,
  FeatureToggleService,
} from 'src/app/core/services/feature/feature-toggle.service';

export interface FeatureTogglesDialogData {
  input: {
    featureFlags$: typeof FeatureToggleService.prototype.allToggles$;
  };
  output: {
    onOverrideChangeEventHandler: (flag: FeatureFlagKey) => void;
  };
}

@Component({
  imports: [MatDialogModule, MatInputModule, MatOptionModule, MatSelectModule],
  template: ` <div class="dialog-wrapper">
    <mat-dialog-content>
      <h2 mat-dialog-title>Feature flags:</h2>
      @for (flag of flags(); track flag) {
        <div class="flag-wrapper">
          <span class="key">{{ flag.id }}</span
          >:
          <span (click)="onClick(flag.id)" class="value" [class]="!!flag.value ? 'active' : 'inactive'">{{
            flag.value.value
          }}</span>
          @if (getIsNotNullable(flag.value.override)) {
            (override: <span class="override">{{ flag.value.override }}</span
            >)
          }
        </div>
      }
    </mat-dialog-content>
  </div>`,
  styles: `
    .dialog-wrapper {
      max-width: 400px;
    }
    .flag-wrapper {
      margin-top: 16px;
    }
    .active {
      color: green;
    }
    .inactive {
      color: gray;
    }
    .key {
      color: blue;
    }
    .value {
      &:hover {
        cursor: pointer;
      }
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FeatureTogglesDialog {
  getIsNotNullable = getIsNotNullable;
  data: FeatureTogglesDialogData = inject(MAT_DIALOG_DATA);

  readonly flags = toSignal(this.data.input.featureFlags$.pipe(map(toListOfObjectsWithId)), {
    initialValue: [],
  });

  onClick(key: FeatureFlagKey): void {
    this.data.output.onOverrideChangeEventHandler(key);
  }
}

function toListOfObjectsWithId(value: FeatureFlagConfig): {
  id: FeatureFlagKey;
  value: FeatureFlagConfig[keyof FeatureFlagConfig];
}[] {
  return Object.entries(value).map(val => ({ id: val[0] as FeatureFlagKey, value: val[1] }));
}
